import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Alert,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { Text, Surface, Button, Portal, Dialog, ActivityIndicator } from 'react-native-paper';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RouteProp } from '@react-navigation/native';
import { RootStackParamList } from '../types/navigation';
import helpService, { HelpRequest, Response } from '../services/helpService';
import authService from '../services/authService';
import SlidingMenu from '../components/SlidingMenu';
import MaxWidthContainer from '../components/MaxWidthContainer';
import HeaderBar from '../components/HeaderBar';
import { brandPrimary, brandPrimarySoft, brandButton } from '../theme/paperTheme';

type ViewResponsesScreenNavigationProp = NativeStackNavigationProp<
  RootStackParamList,
  'ViewResponses'
>;
type ViewResponsesScreenRouteProp = RouteProp<RootStackParamList, 'ViewResponses'>;

type ViewResponsesScreenProps = {
  navigation: ViewResponsesScreenNavigationProp;
  route: ViewResponsesScreenRouteProp & {
    params: {
      helpRequest: HelpRequest;
      shouldRefresh?: boolean;
    };
  };
};

const ViewResponsesScreen: React.FC<ViewResponsesScreenProps> = ({ navigation, route }) => {
  const { helpRequest, shouldRefresh } = route.params;
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;

  const [responses, setResponses] = useState<Response[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [responseToDelete, setResponseToDelete] = useState<string | null>(null);

  useEffect(() => {
    fetchResponses();
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (shouldRefresh) {
      fetchResponses();
    }
  }, [shouldRefresh]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      fetchResponses();
    });
    return unsubscribe;
  }, [navigation]);

  const fetchResponses = async () => {
    try {
      setIsLoading(true);
      const fetchedResponses = await helpService.getResponses(helpRequest._id);
      setResponses(fetchedResponses);
    } catch (error) {
      console.error('Error fetching responses:', error);
      Alert.alert('Error', 'Failed to fetch responses. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const user = await authService.getCurrentUser();
      if (user) {
        const userId = user.id || user._id;
        if (userId) {
          setCurrentUserId(userId);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    }
  };

  const handleAddResponse = () => {
    navigation.navigate('HelpResponse', { helpRequest });
  };

  const handleDeleteResponse = async (responseId: string) => {
    try {
      await helpService.deleteResponse(responseId);
      await fetchResponses();
      Alert.alert('Success', 'Response deleted successfully');
    } catch (error: unknown) {
      const ax = error as {
        message?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      let errorMessage = 'Failed to delete response';
      if (ax.response?.status === 404) {
        errorMessage = 'Response not found';
      } else if (ax.response?.status === 401) {
        errorMessage = 'Not authorized to delete this response';
      } else if (ax.response?.data?.message) {
        errorMessage = ax.response.data.message;
      }
      Alert.alert('Error', errorMessage);
    }
  };

  const showDeleteConfirmation = (responseId: string) => {
    setResponseToDelete(responseId);
    setDeleteDialogVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (responseToDelete) {
      await handleDeleteResponse(responseToDelete);
      setDeleteDialogVisible(false);
      setResponseToDelete(null);
    }
  };

  const renderResponse = ({ item }: { item: Response }) => {
    const responseUserId = item.user.id || item.user._id;

    return (
      <Surface style={styles.responseCard} elevation={0}>
        <Text style={styles.responseContent}>{item.content}</Text>
        <View style={styles.responseMeta}>
          <Text style={styles.responseAuthor}>{item.user.displayName}</Text>
          <View style={styles.responseActions}>
            <Text style={styles.responseDate}>
              {new Date(item.createdAt).toLocaleDateString()}
            </Text>
            {responseUserId === currentUserId && (
              <TouchableOpacity
                onPress={() => showDeleteConfirmation(item._id)}
                style={styles.deleteButton}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Text style={styles.deleteText}>Delete</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </Surface>
    );
  };

  const toggleMenu = () => {
    setIsMenuVisible(!isMenuVisible);
  };

  const listHeader = (
    <>
      <Text style={styles.screenLabel}>Your request</Text>
      <Text style={styles.screenTitle}>Community responses</Text>

      <Surface style={styles.questionCard} elevation={0}>
        <Text style={styles.questionTitle}>{helpRequest.title}</Text>
        {helpRequest.category ? (
          <Text style={styles.questionCategory}>{helpRequest.category}</Text>
        ) : null}
        <Text style={styles.questionDescription}>{helpRequest.description}</Text>
        <View style={styles.questionMeta}>
          <Text style={styles.questionAuthor}>{helpRequest.user.displayName}</Text>
          <Text style={styles.questionDate}>
            {new Date(helpRequest.createdAt).toLocaleDateString()}
          </Text>
        </View>
      </Surface>

      <View style={styles.responsesHeader}>
        <Text style={styles.responsesTitle}>
          {responses.length > 0
            ? `${responses.length} response${responses.length === 1 ? '' : 's'}`
            : 'No responses yet'}
        </Text>
        <Button
          mode="contained"
          onPress={handleAddResponse}
          style={styles.respondButton}
          buttonColor={brandButton}
          textColor="#ffffff"
        >
          Add response
        </Button>
      </View>

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={brandPrimary} />
          <Text style={styles.loadingText}>Loading responses…</Text>
        </View>
      ) : responses.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyEmoji}>💬</Text>
          <Text style={styles.emptyTitle}>No responses yet</Text>
          <Text style={styles.emptyText}>
            Be the first to respond, or check back as the community weighs in.
          </Text>
        </View>
      ) : null}
    </>
  );

  return (
    <View style={styles.container}>
      <Surface style={styles.surface}>
        <View style={styles.headerWrap}>
          <HeaderBar
            rightContent={
              <TouchableOpacity style={styles.menuButton} onPress={toggleMenu}>
                <Text style={styles.menuText}>☰</Text>
              </TouchableOpacity>
            }
            onLogoPress={() => navigation.navigate('Home')}
            style={styles.headerBar}
          />
        </View>

        <MaxWidthContainer style={isDesktop ? styles.wideContainer : undefined}>
          <FlatList
            ListHeaderComponent={listHeader}
            data={isLoading || responses.length === 0 ? [] : responses}
            renderItem={renderResponse}
            keyExtractor={(item) => item._id}
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator={false}
          />
        </MaxWidthContainer>
      </Surface>

      <SlidingMenu isVisible={isMenuVisible} onClose={toggleMenu} />

      <Portal>
        <Dialog visible={deleteDialogVisible} onDismiss={() => setDeleteDialogVisible(false)}>
          <Dialog.Title>Delete response</Dialog.Title>
          <Dialog.Content>
            <Text style={styles.dialogText}>Are you sure you want to delete this response?</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeleteDialogVisible(false)} textColor={brandPrimary}>
              Cancel
            </Button>
            <Button onPress={handleConfirmDelete} textColor="#d32f2f">
              Delete
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F4F7FB',
  },
  surface: {
    flex: 1,
    elevation: 0,
    backgroundColor: '#F4F7FB',
  },
  wideContainer: {
    maxWidth: 960,
  },
  headerWrap: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 0 rgba(15, 23, 42, 0.06)' }
      : {
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(15, 23, 42, 0.06)',
        }),
  },
  headerBar: {
    marginHorizontal: 8,
    marginBottom: 0,
  },
  menuButton: {
    width: 52,
    height: 52,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuText: {
    fontSize: 36,
    color: '#1E293B',
  },
  content: {
    paddingHorizontal: 20,
    paddingTop: 28,
    paddingBottom: 48,
    gap: 14,
  },
  screenLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: brandPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  screenTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: '#1E293B',
    letterSpacing: -0.5,
    marginBottom: 20,
    lineHeight: 36,
  },
  questionCard: {
    padding: 20,
    borderRadius: 16,
    marginBottom: 8,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 4px 20px rgba(15, 23, 42, 0.06)' }
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.06,
          shadowRadius: 12,
          elevation: 3,
        }),
  },
  questionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 10,
    lineHeight: 26,
  },
  questionCategory: {
    fontSize: 12,
    fontWeight: '600',
    color: brandPrimary,
    backgroundColor: brandPrimarySoft,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    alignSelf: 'flex-start',
    marginBottom: 12,
    overflow: 'hidden',
  },
  questionDescription: {
    fontSize: 15,
    color: '#475569',
    lineHeight: 22,
    marginBottom: 14,
  },
  questionMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  questionAuthor: {
    fontSize: 13,
    color: '#64748B',
    fontWeight: '500',
  },
  questionDate: {
    fontSize: 13,
    color: '#94A3B8',
  },
  responsesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 8,
    marginBottom: 4,
  },
  responsesTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
    flex: 1,
  },
  respondButton: {
    borderRadius: 999,
  },
  responseCard: {
    padding: 20,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    marginBottom: 0,
  },
  responseContent: {
    fontSize: 15,
    color: '#334155',
    lineHeight: 22,
    marginBottom: 14,
  },
  responseMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  responseActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  responseAuthor: {
    fontSize: 13,
    color: '#64748B',
    fontWeight: '500',
    flex: 1,
  },
  responseDate: {
    fontSize: 13,
    color: '#94A3B8',
  },
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  loadingText: {
    textAlign: 'center',
    color: '#64748B',
    marginTop: 12,
    fontSize: 15,
  },
  emptyContainer: {
    alignItems: 'center',
    padding: 32,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    marginTop: 8,
  },
  emptyEmoji: {
    fontSize: 36,
    marginBottom: 10,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyText: {
    textAlign: 'center',
    color: '#64748B',
    fontSize: 15,
    lineHeight: 22,
    maxWidth: 300,
  },
  deleteButton: {
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  deleteText: {
    color: '#d32f2f',
    fontWeight: '600',
    fontSize: 13,
  },
  dialogText: {
    color: '#475569',
    lineHeight: 22,
  },
});

export default ViewResponsesScreen;
